import * as Sentry from "@sentry/react";
import type { ReactNode } from "react";
import { config } from "../environment";
import { openSupportUrl } from "../lib/support";
import { sessionId } from "../lib/tracing";
import { ErrorPage } from "./ErrorPage";

type FallbackProps = {
  error: unknown;
  componentStack: string;
  eventId: string;
};

function referenceCode(eventId: string): string {
  const event = (eventId || Sentry.lastEventId() || "").slice(0, 8);
  return [config.gitSha.slice(0, 7), sessionId, event || "—"].join(" · ");
}

function copyText({ error, componentStack, eventId }: FallbackProps): string {
  const err = error instanceof Error ? error : undefined;
  const lines = [
    `Error reference: ${referenceCode(eventId)}`,
    `Time: ${new Date().toISOString()}`,
    `URL: ${location.href}`,
    `Build: ${config.gitSha}`,
    `Session: ${sessionId}`,
    `Sentry event: ${eventId || Sentry.lastEventId() || "n/a"}`,
    `User agent: ${navigator.userAgent}`,
    "",
    `Error: ${err ? `${err.name}: ${err.message}` : String(error)}`,
  ];
  if (err?.stack) lines.push("", "Stack:", err.stack);
  if (componentStack) lines.push("", "Component stack:", componentStack.trim());
  return lines.join("\n");
}

function Fallback(props: FallbackProps) {
  return (
    <ErrorPage
      getCopyText={() => copyText(props)}
      primaryAction={{
        label: "Reload",
        onPress: () => window.location.reload(),
      }}
      supportAction={{ label: "Contact support", onPress: openSupportUrl }}
    />
  );
}

export function AppErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <Sentry.ErrorBoundary
      handled={false}
      beforeCapture={(scope) => {
        scope.setTag("errorBoundary", "app");
        scope.setTag("session_id", sessionId);
        scope.setExtra("location", location.href);
      }}
      fallback={(props) => <Fallback {...props} />}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
